import { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Share2, Download, QrCode, Smartphone, X, Check, ArrowDown } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { usePWAInstall } from '../hooks/usePWAInstall';

const SITE_URL = 'https://iomsl.com';

export function ShareQRButton({ variant = 'nav', appButtonSettings }) {
  const [open, setOpen] = useState(false);
  const qrRef = useRef(null);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'DRAIMOE — I.O.M.S.L',
          text: 'Check out DRAIMOE — music, beats, and more',
          url: SITE_URL,
        });
      } catch {}
    } else {
      navigator.clipboard.writeText(SITE_URL);
      toast.success('Link copied to clipboard!');
    }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const hiRes = document.createElement('canvas');
    hiRes.width = 1024;
    hiRes.height = 1024;
    const ctx = hiRes.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(canvas, 0, 0, 1024, 1024);
    const link = document.createElement('a');
    link.download = 'iomsl-qr-code.png';
    link.href = hiRes.toDataURL('image/png');
    link.click();
    toast.success('QR code downloaded!');
  };

  if (variant === 'icon') {
    return (
      <>
        <button onClick={() => setOpen(true)} className="text-[#666] hover:text-[#DAA520] transition-colors" data-testid="qr-share-icon" title="Share / QR Code">
          <QrCode className="w-5 h-5" />
        </button>
        <ShareDialog open={open} onOpenChange={setOpen} qrRef={qrRef} handleShare={handleShare} downloadQR={downloadQR} />
      </>
    );
  }

  if (variant === 'links') {
    return (
      <div className="space-y-3" data-testid="share-qr-section">
        <div className="flex justify-center">
          <div ref={qrRef} className="bg-white p-3 inline-block">
            <QRCodeCanvas value={SITE_URL} size={160} level="H" bgColor="#FFFFFF" fgColor="#0A0A0A" />
          </div>
        </div>
        <p className="text-center text-[#666] text-xs">Scan to visit iomsl.com</p>
        <div className="flex gap-2 justify-center">
          <Button onClick={handleShare} variant="outline" size="sm" className="rounded-none border-[#262626] hover:border-[#DAA520] text-xs" data-testid="share-btn-links">
            <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
          </Button>
          <Button onClick={downloadQR} variant="outline" size="sm" className="rounded-none border-[#262626] hover:border-[#DAA520] text-xs" data-testid="download-qr-links">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Save QR
          </Button>
        </div>
      </div>
    );
  }

  // Nav variant (default) — show by default if no settings saved yet
  const showInNav = appButtonSettings?.enabled === false ? false : appButtonSettings?.show_in_nav !== false;

  return (
    <>
      <div className="flex items-center gap-3" data-testid="share-qr-nav">
        {showInNav && <InstallAppButton variant="nav" settings={appButtonSettings} />}
        <button onClick={handleShare} className="text-[#666] hover:text-white text-xs font-bold uppercase tracking-widest transition-colors flex items-center gap-1.5" data-testid="share-btn-nav">
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
        <button onClick={() => setOpen(true)} className="text-[#666] hover:text-[#DAA520] text-xs font-bold uppercase tracking-widest transition-colors flex items-center gap-1.5" data-testid="qr-btn-nav">
          <QrCode className="w-3.5 h-3.5" /> QR
        </button>
      </div>
      <ShareDialog open={open} onOpenChange={setOpen} qrRef={qrRef} handleShare={handleShare} downloadQR={downloadQR} />
    </>
  );
}

function ShareDialog({ open, onOpenChange, qrRef, handleShare, downloadQR }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-xs">
        <DialogHeader>
          <DialogTitle className="font-cabinet font-black uppercase text-center">SHARE</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-2">
          <div className="flex justify-center">
            <div ref={qrRef} className="bg-white p-4 inline-block">
              <QRCodeCanvas value={SITE_URL} size={200} level="H" bgColor="#FFFFFF" fgColor="#0A0A0A" />
            </div>
          </div>
          <p className="text-center text-[#A3A3A3] text-sm">Scan to visit <span className="text-[#DAA520] font-bold">iomsl.com</span></p>
          <div className="flex gap-2">
            <Button onClick={handleShare} className="flex-1 rounded-none bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A]" data-testid="share-dialog-btn">
              <Share2 className="w-4 h-4 mr-2" /> SHARE LINK
            </Button>
            <Button onClick={downloadQR} variant="outline" className="flex-1 rounded-none border-[#262626]" data-testid="download-qr-dialog-btn">
              <Download className="w-4 h-4 mr-2" /> SAVE QR
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const COLOR_MAP = {
  red: { bg: '#FF3B30', text: '#FFFFFF' },
  gold: { bg: '#DAA520', text: '#0A0A0A' },
  white: { bg: '#FFFFFF', text: '#0A0A0A' },
  green: { bg: '#22C55E', text: '#FFFFFF' },
  blue: { bg: '#3B82F6', text: '#FFFFFF' },
  purple: { bg: '#8B5CF6', text: '#FFFFFF' },
};

/* ── Install App Button ─────────────────────────────────────────── */
export function InstallAppButton({ variant = 'nav', settings }) {
  const { canInstall, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const [dialogOpen, setDialogOpen] = useState(false);

  const btnText = settings?.text || 'Get App';
  const colorId = settings?.color || 'red';
  const colors = COLOR_MAP[colorId] || COLOR_MAP.red;
  const bgImage = settings?.bg_image || '';

  const handleClick = async () => {
    if (isInstalled) {
      toast.success('App is already installed!');
      return;
    }
    if (canInstall) {
      const outcome = await promptInstall();
      if (outcome === 'accepted') {
        toast.success('App installed! Find it on your home screen.');
      }
      return;
    }
    // Fallback: show instructions dialog
    setDialogOpen(true);
  };

  if (isInstalled) return null;

  if (variant === 'nav') {
    return (
      <>
        <button
          onClick={handleClick}
          className={`hover:text-white text-xs font-bold uppercase tracking-widest transition-colors flex items-center gap-1.5 ${!bgImage ? 'animate-pulse hover:animate-none' : 'hover:opacity-80'}`}
          style={bgImage ? {
            backgroundImage: `url(${bgImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            color: colors.bg,
            textShadow: btnText ? '0 1px 3px rgba(0,0,0,0.8)' : 'none',
            padding: btnText ? '4px 8px' : '0',
            width: btnText ? 'auto' : '28px',
            height: btnText ? 'auto' : '28px',
          } : { color: colors.bg }}
          data-testid="install-app-nav-btn"
        >
          {btnText ? <><Smartphone className="w-3.5 h-3.5" /> {btnText}</> : (!bgImage && <Smartphone className="w-3.5 h-3.5" />)}
        </button>
        <InstallInstructionsDialog open={dialogOpen} onOpenChange={setDialogOpen} isIOS={isIOS} />
      </>
    );
  }

  // Full button variant (for Links page etc.)
  return (
    <>
      <button
        onClick={handleClick}
        className="inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wider hover:opacity-90 transition-opacity cursor-pointer"
        style={bgImage ? {
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: colors.bg,
          textShadow: btnText ? '0 1px 4px rgba(0,0,0,0.9)' : 'none',
          padding: btnText ? '12px 24px' : '0',
          width: btnText ? 'auto' : '60px',
          height: btnText ? 'auto' : '60px',
          fontSize: '14px',
        } : {
          backgroundColor: colors.bg,
          color: colors.text,
          padding: '10px 20px',
          fontSize: '14px',
        }}
        data-testid="install-app-btn"
      >
        {btnText ? <><Smartphone className="w-4 h-4" /> {btnText.toUpperCase()}</> : (!bgImage && <Smartphone className="w-4 h-4" />)}
      </button>
      <InstallInstructionsDialog open={dialogOpen} onOpenChange={setDialogOpen} isIOS={isIOS} />
    </>
  );
}

function InstallInstructionsDialog({ open, onOpenChange, isIOS }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-cabinet font-black uppercase text-center flex items-center justify-center gap-2">
            <Smartphone className="w-5 h-5 text-[#FF3B30]" />
            GET THE APP
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <p className="text-[#A3A3A3] text-sm text-center">
            Add <span className="text-[#DAA520] font-bold">IOMSL</span> to your home screen for instant access — works like a real app, no app store needed.
          </p>

          {isIOS ? (
            <div className="space-y-3" data-testid="ios-install-steps">
              <p className="text-xs text-[#FF3B30] font-bold uppercase tracking-wider text-center">iPhone / iPad</p>
              <div className="space-y-2">
                <Step num={1} text={<>Tap the <strong>Share</strong> button <ShareIcon /> in Safari</>} />
                <Step num={2} text={<>Scroll down and tap <strong>"Add to Home Screen"</strong></>} />
                <Step num={3} text={<>Tap <strong>"Add"</strong> — done!</>} />
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="android-install-steps">
              <p className="text-xs text-[#FF3B30] font-bold uppercase tracking-wider text-center">Android / Desktop</p>
              <div className="space-y-2">
                <Step num={1} text={<>Open <strong>iomsl.com</strong> in Chrome or Edge</>} />
                <Step num={2} text={<>Tap the <strong>menu</strong> (three dots) at the top right</>} />
                <Step num={3} text={<>Tap <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong></>} />
              </div>
            </div>
          )}

          <div className="border-t border-[#262626] pt-4">
            <p className="text-[#666] text-xs text-center">
              No downloads. No app store. Instant access from your home screen.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Step({ num, text }) {
  return (
    <div className="flex items-start gap-3 bg-[#0A0A0A] border border-[#262626] p-3">
      <span className="w-6 h-6 bg-[#FF3B30] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{num}</span>
      <p className="text-sm text-[#E5E5E5] leading-relaxed">{text}</p>
    </div>
  );
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 inline ml-1 mr-0.5 -mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

// Admin version with larger QR + download
export function AdminQRCode() {
  const qrRef = useRef(null);

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const hiRes = document.createElement('canvas');
    hiRes.width = 2048;
    hiRes.height = 2048;
    const ctx = hiRes.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(canvas, 0, 0, 2048, 2048);
    const link = document.createElement('a');
    link.download = 'iomsl-qr-code-hires.png';
    link.href = hiRes.toDataURL('image/png');
    link.click();
    toast.success('High-res QR code downloaded!');
  };

  return (
    <div className="bg-[#141414] border border-[#262626] p-6 space-y-4" data-testid="admin-qr-section">
      <h3 className="font-cabinet text-lg font-bold uppercase tracking-tight">QR CODE</h3>
      <p className="text-[#A3A3A3] text-sm">Download and use on flyers, social media, merch, and promo materials.</p>
      <div className="flex items-center gap-6">
        <div ref={qrRef} className="bg-white p-4 inline-block">
          <QRCodeCanvas value={SITE_URL} size={180} level="H" bgColor="#FFFFFF" fgColor="#0A0A0A" />
        </div>
        <div className="space-y-3">
          <p className="text-sm font-mono text-[#DAA520]">iomsl.com</p>
          <Button onClick={downloadQR} variant="outline" className="rounded-none border-[#262626]" data-testid="admin-download-qr">
            <Download className="w-4 h-4 mr-2" /> Download Hi-Res QR
          </Button>
          <p className="text-[#666] text-xs">2048x2048px PNG — print-ready</p>
        </div>
      </div>
    </div>
  );
}
